import styled from '@emotion/styled';
import { Dropdown, Menu } from 'antd';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps, useLocation, withRouter } from 'react-router-dom';

import { navigationService } from 'src/config/rootService';
import { Dispatch, RootState } from 'src/config/rootStore';
import { doesUserQualifyForTherapy, getFullName } from 'src/modules/user/selectors/userSelectors';
import { Link } from 'react-router-dom';
import { color, font } from 'src/shared/styles/variables';
import { useLocalizedVersion } from '../hooks/useLocalizedVersion';

import {
    isHrAdminSelector,
    isLookerDashboardIframeVisibleSelector,
} from 'src/modules/admin/selectors/adminSelectors';
import circlesIcon from 'src/public/img/icon-circles.svg';
import programsIcon from 'src/public/img/icon-course-grey.svg';
import faqIcon from 'src/public/img/icon-faq-grey.svg';
import logoutIcon from 'src/public/img/icon-logout-grey.svg';
import meditationsIcon from 'src/public/img/icon-meditations-grey.svg';
import userIcon from 'src/public/img/icon-user-grey.svg';
import logoImg from 'src/public/img/mh-logo-text.svg';

import { config as adminConfig } from 'src/modules/admin/config/config';
import { ComponentTextSmall, dsColors } from 'src/shared/ds';
import { CrisisModal } from '../components/CrisisModal';
import { hexToRgba } from '../helpers/hexToRgba';
import { isCurrentLanguageSupported } from '../helpers/isCurrentLanguageSupported';
import * as S from './styles';
import { SOURCE_TYPES } from 'src/modules/user/models/user';

const BrandImage = styled.img({
    width: '10em',
    cursor: 'pointer',
    marginRight: 10,
});

const SectionLinkWrapper = styled.div({
    marginLeft: 30,
    marginTop: 6,
    display: 'flex',
    cursor: 'pointer',
});

const SectionLinkIcon = styled.img({
    marginBottom: 2,
    width: 16,
    height: 16,
});

interface SectionLinkTextProps {
    active?: boolean;
}

const OldSectionLinkText = styled.span<SectionLinkTextProps>(({ active }) => ({
    marginLeft: 10,
    color: active ? '#4C7DE0' : color.blueBayoux,
    borderBottom: '1px solid transparent',
    borderColor: active ? '#4C7DE0' : 'transparent',
    ...(active ? font.heavy : font.medium)(font.size.base),
}));

const NewSectionLinkText = styled(ComponentTextSmall)<SectionLinkTextProps>(({ active }) => ({
    marginLeft: 10,
    color: active ? dsColors.neutral400 : color.blueBayoux,
    borderBottom: '1px solid transparent',
    borderColor: active ? '#4C7DE0' : 'transparent',
}));

const MenuItem = styled(Menu.Item)({
    paddingTop: 0,
    paddingBottom: 0,

    '&&:hover': {
        backgroundColor: color.white,
    },
});

interface MenuItemTextProps {
    withBorder?: boolean;
}

const OldMenuItemText = styled.p<MenuItemTextProps>(({ withBorder }) => ({
    paddingBottom: 5,
    paddingTop: 10,
    borderBottom: `1px solid ${withBorder ? hexToRgba(dsColors.neutral400, 0.6) : 'transparent'}`,
    ...font.heavy(font.size.base),
}));

const NewMenuItemText = styled(ComponentTextSmall)<MenuItemTextProps>(({ withBorder }) => ({
    paddingBottom: 5,
    paddingTop: 10,
    borderBottom: `1px solid ${withBorder ? hexToRgba(color.casper, 0.6) : 'transparent'}`,
    color: dsColors.neutral400,
}));

const LogoutLinkWrapper = styled(SectionLinkWrapper)({
    marginTop: 0,
    marginLeft: 20,
});

const AdminExitIcon = styled(SectionLinkIcon)({
    transform: 'rotate(180deg)',
});

const AdminBadge = styled(Link)(
    {
        borderRadius: 5,
        padding: '9px 12px 6px 15px',
        backgroundColor: color.whiteIce,
        color: color.ebonyClay,
        '&:hover': {
            color: color.ebonyClay,
        },
    },
    font.heavy(font.size.base),
);

const MainNavigation = styled.div({
    display: 'flex',
    flexWrap: 'wrap',
});

enum HeaderTestID {
    ContactUs = 'HeaderTestID.ContactUs',
    LogOut = 'HeaderTestID.LogOut',
    Circles = 'HeaderTestID.Circles',
    Meditations = 'HeaderTestID.Meditations',
    FAQ = 'HeaderTestID.FAQ',
    Programs = 'HeaderTestID.Programs',
    BackToAdminDashboard = 'HeaderTestID.BackToAdminDashboard',
}

type Props = ReturnType<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps> &
    RouteComponentProps;

const HeaderContainer: React.FC<Props> = props => {
    const { t } = useTranslation();
    const { fullName } = props;
    const { pathname } = useLocation();
    const [showCrisisModal, setShowCrisisModal] = useState(false);

    const [localizedVersion] = useLocalizedVersion();
    const isLanguageSupported = isCurrentLanguageSupported();
    const flags = useFlags();

    const dispatch = useDispatch<Dispatch>();

    const qualifiesForTherapy = useSelector(doesUserQualifyForTherapy);
    const isLookerDashboardOpened = useSelector(isLookerDashboardIframeVisibleSelector);
    const isHrAdmin = useSelector(isHrAdminSelector);

    const redirect = (url: string) => () => setTimeout(() => navigationService.navigate(url)); // workaround => react-select used by antd doesn't support react.lazy yet

    const isAdmin = pathname.includes('admin');
    const acceptedAdminTerms = localStorage.getItem(adminConfig.termsLocalStorageKey);
    const isAdminTerms = isAdmin && !acceptedAdminTerms;

    const shouldSeeNewHomeScreen = flags.homeScreenRevampPhase1Web && !localizedVersion;

    const SectionLinkText = shouldSeeNewHomeScreen ? NewSectionLinkText : OldSectionLinkText;

    const MenuItemText = flags.homeScreenRevampPhase1Web ? NewMenuItemText : OldMenuItemText;
    const UserName = flags.homeScreenRevampPhase1Web ? S.NewUserName : S.UserName;

    const renderLinks = () => (
        <>
            {shouldSeeNewHomeScreen && (
                <SectionLinkWrapper
                    onClick={() => navigationService.navigate('/resources')}
                    data-testid={HeaderTestID.Programs}
                >
                    <SectionLinkIcon src={programsIcon} />
                    <SectionLinkText
                        active={['/resources', '/programs', '/meditations'].includes(
                            props.location.pathname,
                        )}
                    >
                        {t('shared:header.resources')}
                    </SectionLinkText>
                </SectionLinkWrapper>
            )}
            {!localizedVersion && !shouldSeeNewHomeScreen && (
                <SectionLinkWrapper
                    onClick={() => navigationService.navigate('/programs')}
                    data-testid={HeaderTestID.Programs}
                >
                    <SectionLinkIcon src={programsIcon} />
                    <SectionLinkText active={props.location.pathname == '/programs'}>
                        {t('shared:header.programs')}
                    </SectionLinkText>
                </SectionLinkWrapper>
            )}

            {isLanguageSupported && !shouldSeeNewHomeScreen && (
                <SectionLinkWrapper
                    onClick={() => navigationService.navigate('/meditations')}
                    data-testid={HeaderTestID.Meditations}
                >
                    <SectionLinkIcon src={meditationsIcon} />
                    <SectionLinkText active={props.location.pathname == '/meditations'}>
                        {t('shared:header.meditations')}
                    </SectionLinkText>
                </SectionLinkWrapper>
            )}

            {!localizedVersion && (
                <SectionLinkWrapper
                    onClick={() =>
                        window.open(
                            'https://circles.modernhealth.com/?utm_source=modern-health&utm_medium=web-app',
                            '_blank',
                        )
                    }
                    data-testid={HeaderTestID.Circles}
                >
                    <SectionLinkIcon src={circlesIcon} />
                    <SectionLinkText>{t('shared:header.circles')}</SectionLinkText>
                </SectionLinkWrapper>
            )}

            {!localizedVersion && (
                <SectionLinkWrapper
                    onClick={() =>
                        window.open('https://support.modernhealth.com/hc/en-us', '_blank')
                    }
                    data-testid={HeaderTestID.FAQ}
                >
                    <SectionLinkIcon src={faqIcon} />
                    <SectionLinkText>{t('shared:header.faq')}</SectionLinkText>
                </SectionLinkWrapper>
            )}
        </>
    );
    return (
        <S.AppHeader className={`simplifiedForm-header loggedIn`} data-testid="appHeader">
            <S.HeaderInnerWrapper className="header-inner">
                <MainNavigation>
                    <BrandImage src={logoImg} onClick={() => navigationService.navigate('/')} />
                    {isAdmin && <AdminBadge to="/admin">{t('admin:admin_view')}</AdminBadge>}
                    {!isAdmin && renderLinks()}
                </MainNavigation>

                {isLookerDashboardOpened && (
                    <LogoutLinkWrapper
                        onClick={() => dispatch.admin.setLookerDashboardOpened(false)}
                        data-testid={HeaderTestID.BackToAdminDashboard}
                    >
                        <AdminExitIcon src={logoutIcon} />
                        <SectionLinkText>
                            {t('admin:dashboard.back_to_admin_dashboard')}
                        </SectionLinkText>
                    </LogoutLinkWrapper>
                )}
                {!isLookerDashboardOpened && !isAdminTerms && fullName.length > 1 && (
                    <S.UserActionsWrapper className="userActions-wrapper">
                        <Dropdown
                            overlay={
                                <Menu>
                                    <MenuItem key="0" onClick={redirect('/profile')}>
                                        <MenuItemText withBorder>
                                            {t('shared:header.profile')}
                                        </MenuItemText>
                                    </MenuItem>
                                    {isHrAdmin && (
                                        <MenuItem
                                            key="1"
                                            onClick={redirect(isAdmin ? '/dashboard' : '/admin')}
                                        >
                                            <MenuItemText withBorder>
                                                {t(
                                                    isAdmin
                                                        ? 'admin:switch_to_member_view'
                                                        : 'admin:switch_to_admin_view',
                                                )}
                                            </MenuItemText>
                                        </MenuItem>
                                    )}
                                    {qualifiesForTherapy ? (
                                        <MenuItem
                                            key="2"
                                            onClick={redirect('/work-with-a-therapist')}
                                        >
                                            <MenuItemText withBorder>
                                                {t('shared:header.request_therapy')}
                                            </MenuItemText>
                                        </MenuItem>
                                    ) : null}

                                    {flags?.inviteDependents ? (
                                        <MenuItem
                                            key="3"
                                            onClick={dispatch.user.showInviteDependentModal}
                                        >
                                            <MenuItemText withBorder>
                                                {t('shared:header.invite_dependent')}
                                            </MenuItemText>
                                        </MenuItem>
                                    ) : null}

                                    <MenuItem
                                        key="4"
                                        onClick={() =>
                                            window.open(
                                                'https://support.modernhealth.com/hc/requests/new',
                                                '_blank',
                                            )
                                        }
                                        data-testid={HeaderTestID.ContactUs}
                                    >
                                        <MenuItemText withBorder>
                                            {t('shared:header.contact_us')}
                                        </MenuItemText>
                                    </MenuItem>

                                    <MenuItem
                                        key="5"
                                        onClick={() => {
                                            setShowCrisisModal(true);
                                        }}
                                    >
                                        <MenuItemText
                                            css={{ color: color.wildWatermelon }}
                                            withBorder
                                        >
                                            {t('shared:header.crisis_info')}
                                        </MenuItemText>
                                    </MenuItem>
                                    <MenuItem
                                        key="6"
                                        onClick={props.logout}
                                        data-testid={HeaderTestID.LogOut}
                                    >
                                        <MenuItemText>{t('shared:header.log_out')}</MenuItemText>
                                    </MenuItem>
                                </Menu>
                            }
                            trigger={['click']}
                        >
                            <S.UsernameWrapper>
                                <img src={userIcon} alt="user icon" />
                                <UserName>{fullName}</UserName>
                                <S.CaretDownIcon type="caret-down" />
                            </S.UsernameWrapper>
                        </Dropdown>
                    </S.UserActionsWrapper>
                )}
            </S.HeaderInnerWrapper>

            <CrisisModal
                visible={showCrisisModal}
                onClose={() => {
                    setShowCrisisModal(false);
                }}
                source={SOURCE_TYPES.HOME_PAGE}
            />
        </S.AppHeader>
    );
};

const mapStateToProps = (state: RootState) => ({
    fullName: getFullName(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    logout: dispatch.authentication.logout,
});

export const Header = connect(mapStateToProps, mapDispatchToProps)(withRouter(HeaderContainer));
